'use client';

import React, { useState, useEffect } from 'react';
import { Menu, X, User } from 'lucide-react';
import Link from '@/components/AppLink';
import Image from 'next/image';
import { usePathname } from 'next/navigation';

const mainNavLinks = [
  { to: "/practice", label: "Practice" },
  { to: "/roadmaps", label: "Roadmaps" },
  { to: "/cheatsheets", label: "Cheatsheets" },
  { to: "/programming-topics", label: "Programming" },
  { to: "/faqs", label: "Interviews" },
  { to: "/test-topics", label: "Mock Tests" },
  { to: "/saved", label: "Saved" }
];

function CustomNavbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isStudentLoggedIn, setIsStudentLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true);
  const pathname = usePathname();

  useEffect(() => {
    checkStudentAuth();

    const handleAuthChange = () => checkStudentAuth();

    window.addEventListener('studentAuthChange', handleAuthChange);

    return () => {
      window.removeEventListener('studentAuthChange', handleAuthChange);
    };
  }, []);

  const checkStudentAuth = async () => {
    try {
      const apiBaseUrl = (process.env.NEXT_PUBLIC_API_URL || process.env.VITE_API_URL || '').replace(/\/$/, '');
      const response = await fetch(`${apiBaseUrl}/api/student/profile`, {
        credentials: 'include',
      });
      setIsStudentLoggedIn(response.ok);
    } catch {
      setIsStudentLoggedIn(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let frameId = 0;
    const handleScroll = () => {
      if (frameId) return;
      frameId = window.requestAnimationFrame(() => {
        setScrolled(window.scrollY > 50);
        frameId = 0;
      });
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (frameId) window.cancelAnimationFrame(frameId);
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : 'unset';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const isActiveLink = (href: string) => pathname === href || pathname.startsWith(`${href}/`);

  return (
    <>
      <nav className={`study-theme fixed top-0 left-0 right-0 z-30 bg-white border-b border-[var(--study-border)] ${scrolled ? 'shadow-md shadow-[rgba(40,40,31,0.08)]' : ''
        }`} style={{ backgroundColor: '#ffffff' }}>
        <div className="max-w-full mx-auto px-4 sm:px-4 lg:px-4 mr-4 ml-4">
          <div className="flex items-center md:justify-around justify-between ">
            <Link href="/" className="flex-shrink-0 group">
              <Image src="/assets/logo.png" alt="PariksaHub" width={150} height={50} sizes="150px" priority />
            </Link>

            <div className="hidden md:flex items-center gap-7">
              <div className="flex space-x-5">
                {mainNavLinks.map((link) => (
                  <Link
                    key={link.to}
                    href={link.to}
                    className={`site-nav-link px-3 py-2 text-sm font-medium transition-[color,background-color,border-color,opacity,transform] duration-300 hover:scale-105 ${
                      isActiveLink(link.to) ? 'site-nav-link-active' : ''
                    }`}
                    aria-current={isActiveLink(link.to) ? 'page' : undefined}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
              <div className="h-6 w-px bg-[var(--study-border)]" aria-hidden="true" />
              <div className="flex min-h-10 min-w-[176px] items-center justify-end space-x-4">
                {loading ? (
                  <span className="block h-10 w-[176px]" aria-hidden="true" />
                ) : isStudentLoggedIn ? (
                    <Link
                      href="/student/dashboard"
                      className={`site-nav-link flex items-center space-x-2 px-4 py-2 text-sm font-medium transition-[color,background-color,border-color,opacity,transform] duration-300 hover:scale-105 ${
                        isActiveLink('/student/dashboard') ? 'site-nav-link-active' : ''
                      }`}
                      aria-current={isActiveLink('/student/dashboard') ? 'page' : undefined}
                    >
                      <User className="h-5 w-5" />
                      <span>Dashboard</span>
                    </Link>
                ) : (
                    <>
                      <Link
                        href="/student/register"
                        className={`site-nav-link px-3 py-2 text-sm font-medium transition-[color,background-color,border-color,opacity,transform] duration-300 hover:scale-105 ${
                          isActiveLink('/student/register') ? 'site-nav-link-active' : ''
                        }`}
                        aria-current={isActiveLink('/student/register') ? 'page' : undefined}
                      >
                        Register
                      </Link>
                      <Link
                        href="/student/login"
                        className="study-button-primary px-4 py-2 rounded-lg text-sm font-medium transition-[color,background-color,border-color,opacity,transform] duration-300 hover:scale-105"
                      >
                        Login
                      </Link>
                    </>
                )}
              </div>
            </div>

            <button
              onClick={() => setIsOpen(true)}
              className="md:hidden study-muted p-2 rounded-lg hover:text-[var(--study-primary)] hover:bg-[var(--study-primary-soft)] transition-[color,background-color,border-color,opacity,transform] duration-300 hover:scale-110"
            >
              <span className="sr-only">Open menu</span>
              <Menu size={24} />
            </button>
          </div>
        </div>
      </nav>

      <div className={`fixed inset-0 z-50 md:hidden transition-[color,background-color,border-color,opacity,transform] duration-300 ease-in-out ${isOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}>
        <div
          className="fixed inset-0 bg-black/50 transition-opacity duration-300 ease-in-out"
          onClick={() => setIsOpen(false)}
        />

        <div className={`study-theme fixed inset-y-0 right-0 w-[280px] bg-white shadow-xl transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : 'translate-x-full'
          }`} style={{ backgroundColor: '#ffffff' }}>
          <div className="p-4 flex justify-end">
            <button
              onClick={() => setIsOpen(false)}
              className="study-muted p-2 rounded-lg hover:text-[var(--study-primary)] hover:bg-[var(--study-primary-soft)] transition-[color,background-color,border-color,opacity,transform] duration-300 hover:scale-110"
            >
              <span className="sr-only">Close menu</span>
              <X size={24} />
            </button>
          </div>

          <div className="px-4 py-2">
            {mainNavLinks.map((link) => (
              <Link
                key={link.to}
                href={link.to}
                onClick={() => setIsOpen(false)}
                className={`site-nav-link site-nav-link-mobile block w-full text-left py-3 px-4 text-base font-medium rounded-lg transition-[color,background-color,border-color,opacity,transform] duration-300 hover:scale-105 ${
                  isActiveLink(link.to) ? 'site-nav-link-mobile-active' : ''
                }`}
                aria-current={isActiveLink(link.to) ? 'page' : undefined}
              >
                {link.label}
              </Link>
            ))}
            {!loading && (
              <div className="border-t border-[var(--study-border)] mt-4 pt-4">
                {isStudentLoggedIn ? (
                  <Link
                    href="/student/dashboard"
                    onClick={() => setIsOpen(false)}
                    className={`site-nav-link site-nav-link-mobile flex items-center space-x-2 w-full text-left py-3 px-4 text-base font-medium rounded-lg transition-[color,background-color,border-color,opacity,transform] duration-300 ${
                      isActiveLink('/student/dashboard') ? 'site-nav-link-mobile-active' : ''
                    }`}
                    aria-current={isActiveLink('/student/dashboard') ? 'page' : undefined}
                  >
                    <User className="h-5 w-5" />
                    <span>Dashboard</span>
                  </Link>
                ) : (
                  <>
                    <Link
                      href="/student/register"
                      onClick={() => setIsOpen(false)}
                      className={`site-nav-link site-nav-link-mobile block w-full text-left py-3 px-4 text-base font-medium rounded-lg transition-[color,background-color,border-color,opacity,transform] duration-300 ${
                        isActiveLink('/student/register') ? 'site-nav-link-mobile-active' : ''
                      }`}
                      aria-current={isActiveLink('/student/register') ? 'page' : undefined}
                    >
                      Register
                    </Link>
                    <Link
                      href="/student/login"
                      onClick={() => setIsOpen(false)}
                      className="study-button-primary block w-full text-center py-3 px-4 rounded-lg text-base font-medium transition-[color,background-color,border-color,opacity,transform] duration-300 mt-2"
                    >
                      Login
                    </Link>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default CustomNavbar;

